import PropTypes, { shape } from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { errorClear, errorShow } from '../../Utility/Form'
import './Textarea.scss';

/**
 * Primary UI component for user interaction
 */
export const Textarea = ({ label, name, required, step, validationHash, validationMessages, value, resizable, rows, cols, ...props }) => {
  const [inputValue, inputValueSet] = useState(value)
  const [inputIsInvalid, inputIsInvalidSet] = useState(false)
  const [hasInitialised, hasInitialisedSet] = useState(false)
  const input = useRef(null)

  /**
   * The error ID.
   *
   * @type {string}
   */
  const errorId = `${name}_error`

  /**
   * The error message class
   *
   * @type {string}
   */
  const errorSpan = 'b-form-input__error-message'

  /**
   * Handle any form changes.
   *
   * @param {Event} event
   *   The triggered change event
   */
  const handlerInputChange = event => {
    inputValueSet(event.target.value)
  }

  /**
   * Handle any form changes.
   *
   * @param {Event} event
   *   The triggered change event
   */
  const handlerInputBlur = event => {
    if (event.target.value) {
      validate()
    }
  }

  /**
   * Validate the element.
   */
  const validate = () => {
    if (input.current.validity.valid) {
      errorClear(input.current, `.${errorSpan}`)
      inputIsInvalidSet(false)
    }
    else {
      errorShow(input.current, `.${errorSpan}`)
      inputIsInvalidSet(true)
    }
  }

  /**
   * Generate the validation attributes.
   *
   * @type {{}}
   */
  const validationAttributes = validationMessages ? validationMessages.reduce((validationAttributes, current) => {
    if (current.message) {
      validationAttributes[`data-error-${current.type.toLowerCase()}-message`] = current.message
    }

    if (current.type === 'patternMismatch') {
      validationAttributes['pattern'] = current.pattern
    }

    if (current.type === 'rangeUnderflow') {
      validationAttributes['min'] = current.min
    }

    if (current.type === 'rangeOverflow') {
      validationAttributes['max'] = current.max
    }

    if (current.type === 'maxlength') {
      validationAttributes['maxLength'] = current.maxlength
    }

    if (current.type === 'minlength') {
      validationAttributes['minLength'] = current.minlength
    }

    return validationAttributes
  }, {}) : {}

  useEffect(() => {
    if (hasInitialised) {
      const handler = inputIsInvalid ? null : setTimeout(() => props.onInputChange(name, inputValue), 300)
      return () => clearTimeout(handler)
    }
  }, [inputValue, inputIsInvalid])

  useEffect(() => {
    if (validationHash && hasInitialised) {
      validate()
    }
  }, [inputValue, validationHash])

  useEffect(() => {
    hasInitialisedSet(true)
  }, [])

  useEffect(() => {
    if (value !== inputValue) {
      inputValueSet(value)
    }
  }, [value])

  return (
    <div className={'b-form-row'}>
      <div className="b-form-input textarea" data-type='text' data-state={inputIsInvalid ? 'error' : 'ok'}>
        <label htmlFor={name}>{label} {required ? '*' : null}</label>
        <textarea
          className={resizable ? '' : 'area--no-resize'}
          ref={input}
          id={name}
          name={name}
          aria-describedby={errorId}
          aria-required={required}
          aria-invalid={inputIsInvalid}
          step={step}
          required={required}
          value={inputValue}
          onChange={handlerInputChange}
          onKeyUp={handlerInputChange}
          onBlur={handlerInputBlur}
          rows={rows || 4}
          cols={cols || 50}
          {...validationAttributes}
        />

        {required || Object.keys(validationAttributes).length > 0 ?
          <div className="b-form-input__error-wrapper">
            <span className={errorSpan} id={errorId} aria-live="assertive" />
          </div> : null}
      </div>
    </div>
  )
}

Textarea.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  step: PropTypes.string,
  validationHash: PropTypes.string,
  validationMessages: PropTypes.arrayOf(
    shape({
      type: PropTypes.string,
      message: PropTypes.string,
      pattern: PropTypes.string,
    })
  ),
  value: PropTypes.string,
  resizable: PropTypes.bool,
}
